import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Heart, MessageCircle, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';

// Lightweight, low-friction guest entry. A logged-out visitor can start a private
// introductory chat here WITHOUT seeing /auth. Consent + emergency ack are required.
export default function GuestChatEntry() {
  const navigate = useNavigate();
  const { user, guestLogin } = useContext(AuthContext);
  const [displayName, setDisplayName] = useState('');
  const [concern, setConcern] = useState('');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptEmergency, setAcceptEmergency] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // A logged-in Care Seeker should continue via the existing authenticated flow.
  useEffect(() => {
    if (user && user.role === 'care_seeker') {
      sessionStorage.setItem('veyonika_intent', 'chat');
      navigate('/seeker-dashboard', { replace: true });
    }
  }, [user, navigate]);

  const canStart = acceptPrivacy && acceptEmergency && !submitting;

  const start = async () => {
    if (!canStart) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/guest/start`, {
        display_name: displayName.trim() || null,
        concern: concern.trim() || null,
        accept_privacy: acceptPrivacy,
        accept_emergency: acceptEmergency,
      });
      const { token, session_id, masked_id, display_name } = res.data;
      guestLogin(token, { role: 'guest', id: masked_id, masked_id, name: display_name, is_guest: true });
      navigate(`/chat/${session_id}`, { replace: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not start the chat. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-veyonika-bg flex flex-col items-center justify-center px-4 py-10" data-testid="guest-chat-entry">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <Heart className="text-veyonika-maroon" size={26} />
          <span className="text-2xl font-heading font-semibold text-veyonika-maroon">Veyonika</span>
        </div>

        <div className="veyonika-card p-6 sm:p-8">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-veyonika-rose mb-4 mx-auto">
            <MessageCircle className="text-veyonika-maroonHover" size={22} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-center text-veyonika-dark mb-2">
            Start a private chat
          </h1>
          <p className="text-center text-[#7A625C] leading-relaxed mb-6 text-sm">
            No account needed to begin. Share only what you're comfortable with — you can create an
            account later to continue, save your conversation, or access resources.
          </p>

          <label className="block text-sm font-medium text-veyonika-dark mb-1">How would you like us to address you? <span className="text-[#A0AEC0] font-normal">(optional)</span></label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Sam"
            data-testid="guest-display-name"
            className="w-full px-4 py-3 mb-4 rounded-xl border border-[#E7D6D0] focus:outline-none focus:ring-2 focus:ring-veyonika-maroon/30"
          />

          <label className="block text-sm font-medium text-veyonika-dark mb-1">What would you like support with today? <span className="text-[#A0AEC0] font-normal">(optional)</span></label>
          <textarea
            value={concern}
            onChange={(e) => setConcern(e.target.value)}
            placeholder="A few words help your responder understand how to support you."
            rows={2}
            data-testid="guest-concern"
            className="w-full px-4 py-3 mb-4 rounded-xl border border-[#E7D6D0] focus:outline-none focus:ring-2 focus:ring-veyonika-maroon/30 resize-none"
          />

          {/* Emergency disclaimer — shown before the chat begins */}
          <div className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-[#FFF6EC] border border-[#F1D6A8]" data-testid="guest-emergency-disclaimer">
            <AlertTriangle size={18} className="text-[#B7791F] mt-0.5 shrink-0" />
            <p className="text-xs text-[#7A5A1E] leading-relaxed">
              Veyonika is not an emergency service. If you or someone else is in immediate danger,
              please contact local emergency services or a crisis helpline right away.
            </p>
          </div>

          <label className="flex items-start gap-3 mb-3 cursor-pointer">
            <input type="checkbox" checked={acceptEmergency} onChange={(e) => setAcceptEmergency(e.target.checked)} data-testid="guest-accept-emergency" className="mt-1 w-4 h-4 accent-[#7D202B]" />
            <span className="text-sm text-[#5A4741]">I understand this is not an emergency service.</span>
          </label>

          <label className="flex items-start gap-3 mb-5 cursor-pointer">
            <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} data-testid="guest-accept-privacy" className="mt-1 w-4 h-4 accent-[#7D202B]" />
            <span className="text-sm text-[#5A4741]">I accept the privacy policy and terms.</span>
          </label>

          {error && (
            <p className="text-sm text-red-600 mb-3" data-testid="guest-error">{error}</p>
          )}

          <button onClick={start} disabled={!canStart} data-testid="guest-start-btn" className="btn-pill w-full mb-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
            {submitting ? 'Connecting…' : 'Begin private chat'}
          </button>

          <button onClick={() => navigate('/auth')} data-testid="guest-have-account" className="w-full text-center py-2 text-veyonika-maroon hover:text-veyonika-maroonHover font-medium transition-colors">
            Already have an account? Log in
          </button>
        </div>

        <p className="flex items-center justify-center gap-1.5 text-xs text-[#7A625C] mt-5">
          <ShieldCheck size={14} className="text-veyonika-maroonHover" />
          Confidential &amp; private. You are not alone.
        </p>
      </div>
    </div>
  );
}
